/**
 * Hai Theme - Global JavaScript
 * 移动端优先、速度最快、功能强大*
 * 核心功能:
 * - 图片懒加载 * - AJAX购物车 * - 预测搜索
 * - 性能监控
 */

(function () {
  'use strict';

  // ===================================
  // 全局工具函数
  // ===================================

  const Hai = window.Hai || {};

  // 防抖函数 - 优化性能
  Hai.debounce = function (func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  // 节流函数 - 优化滚动性能
  Hai.throttle = function (func, limit) {
    let inThrottle;
    return function (...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  };

  // Fetch API 封装
  Hai.fetchJSON = async function (url, options = {}) {
    try {
      const response = await fetch(url, {
        method: options.method || 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...options.headers
        },
        body: options.body ? JSON.stringify(options.body) : null
      });

      if (!response.ok) {
        let errorDetail = '';
        try {
          const errorPayload = await response.json();
          if (errorPayload && typeof errorPayload === 'object') {
            errorDetail = errorPayload.description || errorPayload.message || JSON.stringify(errorPayload);
          }
        } catch (jsonError) {
          try {
            errorDetail = (await response.text() || '').trim();
          } catch (textError) {
            errorDetail = '';
          }
        }

        const message = errorDetail
          ? `HTTP error! status: ${response.status} - ${errorDetail}`
          : `HTTP error! status: ${response.status}`;
        throw new Error(message);
      }

      return await response.json();
    } catch (error) {
      console.error('Fetch error:', error);
      throw error;
    }
  };

  Hai.toCartJsonUrl = function (url) {
    if (!url) return url;

    const raw = String(url);
    const queryIndex = raw.indexOf('?');
    const path = queryIndex >= 0 ? raw.slice(0, queryIndex) : raw;
    const query = queryIndex >= 0 ? raw.slice(queryIndex) : '';
    const normalizedPath = path.replace(/\/+$/, '');
    const withJson = /\.js$/i.test(normalizedPath) ? normalizedPath : `${normalizedPath}.js`;

    return `${withJson}${query}`;
  };

  // 格式化货币 
  Hai.formatMoney = function (cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }

    const money = (cents / 100).toFixed(2);
    const formatString = format || Hai.settings.moneyFormat;

    return formatString.replace(/\{\{\s*amount\s*\}\}/, money);
  };

  // ===================================
  // 图片懒加载优化  // ===================================

  Hai.lazyImages = {
    init() {
      // 使用原生 Intersection Observer
      if ('IntersectionObserver' in window) {
        const imageObserver = new IntersectionObserver((entries, observer) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const img = entry.target;

              // 加载真实图片
              if (img.dataset.src) {
                img.src = img.dataset.src;
              }

              if (img.dataset.srcset) {
                img.srcset = img.dataset.srcset;
              }

              // 添加加载完成              
              img.classList.add('loaded');
              img.classList.remove('hai-img-lazy');

              // 停止观察
              observer.unobserve(img);
            }
          });
        }, {
          rootMargin: '50px 0px', // 提前50px开始加载         
          // threshold: 0.01
        });

        // 观察所有懒加载图片
        document.querySelectorAll('.hai-img-lazy').forEach(img => {
          imageObserver.observe(img);
        });
      }
    }
  };

  // ===================================
  // AJAX 购物车功能  // ===================================

  Hai.cart = {
    // 添加到购物车
    async addItem(id, quantity = 1, properties = {}, extra = {}) {
      try {
        const data = {
          id: id,
          quantity: quantity,
          properties: properties,
          ...extra
        };

        const response = await Hai.fetchJSON(Hai.toCartJsonUrl(Hai.routes.cartAdd), {
          method: 'POST',
          body: data
        });

        // 触发自定义事件       
        document.dispatchEvent(new CustomEvent('hai:cart:add', {
          detail: response
        }));

        return response;
      } catch (error) {
        console.error('Add to cart error:', error);
        throw error;
      }
    },

    // 一次性添加多条 line item
    async addItems(items, extra = {}) {
      const data = {
        items,
        ...extra
      };

      const response = await Hai.fetchJSON(Hai.toCartJsonUrl(Hai.routes.cartAdd), {
        method: 'POST',
        body: data
      });

      document.dispatchEvent(new CustomEvent('hai:cart:add', {
        detail: response
      }));

      return response;
    },

    // 更新购物车   
    async updateItem(line, quantity) {
      try {
        const data = {
          line: line,
          quantity: quantity
        };

        const response = await Hai.fetchJSON(Hai.toCartJsonUrl(Hai.routes.cartChange), {
          method: 'POST',
          body: data
        });

        // 触发自定义事件       
        document.dispatchEvent(new CustomEvent('hai:cart:update', {
          detail: response
        }));

        return response;
      } catch (error) {
        console.error('Update cart error:', error);
        throw error;
      }
    },

    // 获取购物车状态   
    async getCart() {
      try {
        return await Hai.fetchJSON(Hai.toCartJsonUrl(Hai.routes.cart));
      } catch (error) {
        console.error('Get cart error:', error);
        throw error;
      }
    },

    async removeItemWithRelated(lineKey) {
      const normalizedLineKey = String(lineKey || '').trim();
      if (!normalizedLineKey) {
        throw new Error('Missing cart line key');
      }

      const currentCart = await this.getCart();
      const targetLine = currentCart.items.find((item) => String(item.key || '') === normalizedLineKey);

      if (!targetLine) {
        throw new Error('Cart line is no longer available');
      }

      const updates = {
        [targetLine.key]: 0
      };
      const groupId = Hai.addonCartSync && typeof Hai.addonCartSync.getGroupId === 'function'
        ? Hai.addonCartSync.getGroupId(targetLine)
        : '';

      if (groupId && Hai.addonCartSync) {
        currentCart.items.forEach((item) => {
          const parentGroupId = Hai.addonCartSync.getParentGroupId(item);
          const serviceParentGroupId = Hai.addonCartSync.getMomanteeParentGroupId(item);

          if (parentGroupId === groupId || serviceParentGroupId === groupId) {
            updates[item.key] = 0;
          }
        });
      }

      return Hai.fetchJSON(Hai.toCartJsonUrl(Hai.routes.cartUpdate), {
        method: 'POST',
        body: { updates }
      });
    }
  };

  Hai.addonCartSync = {
    inventoryCache: new Map(),

    getLineProperty(line, key) {
      if (!line || !line.properties) return '';
      const raw = line.properties[key];
      if (raw === null || raw === undefined) return '';
      return String(raw).trim();
    },

    isAddonLine(line) {
      return this.getLineProperty(line, '_hai_addon_type') === 'cross_sell';
    },

    isGiftLine(line) {
      return this.getLineProperty(line, '_hai_gift_type') === 'gift_item'
        && this.getLineProperty(line, '_hai_gift_source') === 'product_gift';
    },

    isMomanteeGiftServiceLine(line) {
      return this.getLineProperty(line, '_momantee_gift_service') === 'video_message';
    },

    isRequiredAddon(line) {
      return this.getLineProperty(line, '_hai_addon_required') === 'true';
    },

    getAddonKey(line) {
      return this.getLineProperty(line, '_hai_addon_key');
    },

    getGroupId(line) {
      return this.getLineProperty(line, '_hai_group_id');
    },

    getParentGroupId(line) {
      return this.getLineProperty(line, '_hai_parent_group_id');
    },

    getMomanteeParentGroupId(line) {
      return this.getLineProperty(line, '_momantee_parent_group_id');
    },

    getParentVariantId(line) {
      const value = this.getLineProperty(line, '_hai_addon_parent');
      const parsed = Number(value);
      if (!Number.isFinite(parsed) || parsed <= 0) return 0;
      return Math.round(parsed);
    },

    getAddonHandle(line) {
      return this.getLineProperty(line, '_hai_addon_handle') || (line && line.handle) || '';
    },

    getGiftParentVariantId(line) {
      const value = this.getLineProperty(line, '_hai_gift_parent');
      const parsed = Number(value);
      if (!Number.isFinite(parsed) || parsed <= 0) return 0;
      return Math.round(parsed);
    },

    getMomanteeParentVariantId(line) {
      const value = this.getLineProperty(line, '_momantee_parent_variant_id');
      const parsed = Number(value);
      if (!Number.isFinite(parsed) || parsed <= 0) return 0;
      return Math.round(parsed);
    },

    getGiftQuantityMode(line) {
      const mode = this.getLineProperty(line, '_hai_gift_quantity_mode');
      return mode === 'match_main_quantity' ? 'match_main_quantity' : 'per_order';
    },

    async getProductData(handle) {
      if (!handle) return null;

      if (this.inventoryCache.has(handle)) {
        return this.inventoryCache.get(handle);
      }

      const rootPath = (Hai.routes && Hai.routes.root ? Hai.routes.root : '/').replace(/\/?$/, '/');
      const url = `${rootPath}products/${encodeURIComponent(handle)}.js`;

      try {
        const product = await Hai.fetchJSON(url);
        this.inventoryCache.set(handle, product);
        return product;
      } catch (error) {
        console.warn('Addon inventory fetch failed:', error);
        this.inventoryCache.set(handle, null);
        return null;
      }
    },

    getMaxAvailableQuantity(variant) {
      if (!variant) return Number.POSITIVE_INFINITY;

      if (!variant.inventory_management) {
        return Number.POSITIVE_INFINITY;
      }

      if (variant.inventory_policy === 'continue') {
        return Number.POSITIVE_INFINITY;
      }

      const quantity = Number(variant.inventory_quantity);
      if (!Number.isFinite(quantity)) return 0;
      return Math.max(0, Math.round(quantity));
    },

    async getAddonInventoryCap(line) {
      const handle = this.getAddonHandle(line);
      if (!handle) {
        return Number.POSITIVE_INFINITY;
      }

      const product = await this.getProductData(handle);
      if (!product || !Array.isArray(product.variants)) {
        return Number.POSITIVE_INFINITY;
      }

      const variantId = Number(line && line.id);
      const variant = product.variants.find((item) => Number(item.id) === variantId);
      if (!variant) {
        return Number.POSITIVE_INFINITY;
      }

      return this.getMaxAvailableQuantity(variant);
    },

    async enforce(cart, options = {}) {
      if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) {
        return cart;
      }

      const notify = options.notify !== false;
      const parentQtyByKey = new Map();
      const parentQtyByKeyAndVariant = new Map();
      const parentQtyByVariant = new Map();
      const parentQtyByGroup = new Map();

      cart.items.forEach((line) => {
        if (!this.isAddonLine(line) && !this.isGiftLine(line) && !this.isMomanteeGiftServiceLine(line)) {
          const variantId = Number(line.id);
          const lineQty = Math.max(0, Number(line.quantity) || 0);
          const groupId = this.getGroupId(line);

          if (groupId && lineQty > 0) {
            parentQtyByGroup.set(groupId, (parentQtyByGroup.get(groupId) || 0) + lineQty);
          }

          if (Number.isFinite(variantId) && variantId > 0 && lineQty > 0) {
            parentQtyByVariant.set(variantId, (parentQtyByVariant.get(variantId) || 0) + lineQty);
          }
        }

        const addonKey = this.getAddonKey(line);
        if (!addonKey || this.isAddonLine(line)) return;

        const lineQty = Math.max(0, Number(line.quantity) || 0);
        if (lineQty <= 0) return;

        parentQtyByKey.set(addonKey, (parentQtyByKey.get(addonKey) || 0) + lineQty);

        const variantId = Number(line.id);
        if (Number.isFinite(variantId) && variantId > 0) {
          const mapKey = `${addonKey}::${variantId}`;
          parentQtyByKeyAndVariant.set(mapKey, (parentQtyByKeyAndVariant.get(mapKey) || 0) + lineQty);
        }
      });

      const updates = {};
      const notices = new Set();

      for (let index = 0; index < cart.items.length; index += 1) {
        const line = cart.items[index];
        const lineKey = line.key || String(index + 1);

        if (this.isGiftLine(line)) {
          const currentQty = Math.max(0, Number(line.quantity) || 0);
          const parentGroupId = this.getParentGroupId(line);
          const parentVariantId = this.getGiftParentVariantId(line);
          let parentQty = parentGroupId ? (parentQtyByGroup.get(parentGroupId) || 0) : 0;

          if (!parentGroupId && parentQty <= 0 && parentVariantId > 0) {
            parentQty = parentQtyByVariant.get(parentVariantId) || 0;
          }

          if (parentQty <= 0) {
            if (currentQty !== 0) {
              updates[lineKey] = 0;
            }
            continue;
          }

          const quantityMode = this.getGiftQuantityMode(line);
          const targetQty = quantityMode === 'match_main_quantity' ? parentQty : 1;

          if (targetQty !== currentQty) {
            updates[lineKey] = targetQty;
          }
          continue;
        }

        if (this.isMomanteeGiftServiceLine(line)) {
          const currentQty = Math.max(0, Number(line.quantity) || 0);
          const parentGroupId = this.getMomanteeParentGroupId(line);
          const parentVariantId = this.getMomanteeParentVariantId(line);
          let parentQty = parentGroupId ? (parentQtyByGroup.get(parentGroupId) || 0) : 0;

          if (!parentGroupId && parentQty <= 0 && parentVariantId > 0) {
            parentQty = parentQtyByVariant.get(parentVariantId) || 0;
          }

          if (parentQty <= 0) {
            if (currentQty !== 0) {
              updates[lineKey] = 0;
            }
            continue;
          }

          if (currentQty !== 1) {
            updates[lineKey] = 1;
          }
          continue;
        }

        if (!this.isAddonLine(line)) continue;

        const addonKey = this.getAddonKey(line);
        const currentQty = Math.max(0, Number(line.quantity) || 0);

        if (!addonKey) {
          if (currentQty !== 0) {
            updates[lineKey] = 0;
          }
          continue;
        }

        const parentGroupId = this.getParentGroupId(line);
        const parentVariantId = this.getParentVariantId(line);
        let parentQty = 0;

        if (parentGroupId) {
          parentQty = parentQtyByGroup.get(parentGroupId) || 0;
        }

        if (!parentGroupId && parentQty <= 0 && parentVariantId > 0) {
          const qtyByVariant = parentQtyByKeyAndVariant.get(`${addonKey}::${parentVariantId}`) || 0;
          parentQty = Math.max(parentQty, qtyByVariant);
        }

        if (!parentGroupId && parentQty <= 0) {
          parentQty = parentQtyByKey.get(addonKey) || 0;
        }

        if (parentQty <= 0) {
          if (currentQty !== 0) {
            updates[lineKey] = 0;
          }
          continue;
        }

        if (!this.isRequiredAddon(line)) {
          if (currentQty !== 1) {
            updates[lineKey] = 1;
          }
          continue;
        }

        let targetQty = parentQty;

        if (targetQty > currentQty) {
          const inventoryCap = await this.getAddonInventoryCap(line);
          if (Number.isFinite(inventoryCap)) {
            targetQty = Math.min(targetQty, inventoryCap);
            if (targetQty < parentQty) {
              const title = line.product_title || line.title || 'Add-on';
              notices.add(`Insufficient stock for ${title}. Maximum available quantity is ${targetQty}.`);
            }
          }
        }

        if (targetQty !== currentQty) {
          updates[lineKey] = targetQty;
        }
      }

      if (Object.keys(updates).length === 0) {
        return cart;
      }

      try {
        const nextCart = await Hai.fetchJSON(Hai.toCartJsonUrl(Hai.routes.cartUpdate), {
          method: 'POST',
          body: { updates }
        });

        if (notify && notices.size && Hai.cartNotification && typeof Hai.cartNotification.show === 'function') {
          const firstNotice = Array.from(notices)[0];
          Hai.cartNotification.show(firstNotice);
        }

        return nextCart;
      } catch (error) {
        console.error('Addon cart sync failed:', error);
        return cart;
      }
    }
  };

  // ===================================
  // 购物车通知
  // ===================================

  Hai.cartNotification = {
    init() {
      this.container = document.querySelector('[data-cart-notification]');
      if (!this.container) return;

      this.messageEl = this.container.querySelector('[data-cart-notification-message]');
      this.closeBtn = this.container.querySelector('[data-cart-notification-close]');
      this.hideTimer = null;

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.hide());
      }
    },

    show(message, options = {}) {
      if (!this.container) return;

      if (this.hideTimer) {
        clearTimeout(this.hideTimer);
        this.hideTimer = null;
      }

      if (this.messageEl) {
        this.messageEl.textContent = message;
      }

      this.container.removeAttribute('hidden');

      requestAnimationFrame(() => {
        this.container.classList.add('is-visible');
      });

      const duration = typeof options.duration === 'number' ? options.duration : 4000;

      if (duration > 0) {
        this.hideTimer = setTimeout(() => this.hide(), duration);
      }
    },

    hide() {
      if (!this.container) return;

      this.container.classList.remove('is-visible');

      if (this.hideTimer) {
        clearTimeout(this.hideTimer);
        this.hideTimer = null;
      }

      setTimeout(() => {
        if (!this.container.classList.contains('is-visible')) {
          this.container.setAttribute('hidden', 'hidden');
        }
      }, 200);
    }
  };

  Hai.cartNote = {
    giftSummaryStart: '[MOMANTEE_GIFT_V1]',
    giftSummaryEnd: '[/MOMANTEE_GIFT_V1]',

    init() {
      if (this.initialized) return;

      this.initialized = true;
      this.handleClick = this.handleClick.bind(this);
      this.handleInput = this.handleInput.bind(this);

      document.addEventListener('click', this.handleClick);
      document.addEventListener('input', this.handleInput);
    },

    handleClick(event) {
      const toggle = event.target.closest('[data-cart-note-toggle]');
      if (toggle) {
        event.preventDefault();
        const container = toggle.closest('[data-cart-note]');
        if (!container) return;
        this.toggle(container);
        return;
      }

      const saveButton = event.target.closest('[data-cart-note-save]');
      if (!saveButton) return;

      event.preventDefault();
      const container = saveButton.closest('[data-cart-note]');
      if (!container) return;
      this.save(container);
    },

    handleInput(event) {
      if (!event.target.matches('[data-cart-note-input]')) return;
      this.syncValues(event.target.value, event.target);
    },

    toggle(container, forceOpen) {
      const toggle = container.querySelector('[data-cart-note-toggle]');
      const panel = container.querySelector('[data-cart-note-panel]');
      if (!toggle || !panel) return;

      const shouldOpen = typeof forceOpen === 'boolean'
        ? forceOpen
        : toggle.getAttribute('aria-expanded') !== 'true';

      toggle.setAttribute('aria-expanded', String(shouldOpen));
      container.classList.toggle('is-open', shouldOpen);
      panel.hidden = !shouldOpen;
    },

    syncValues(value, sourceInput) {
      document.querySelectorAll('[data-cart-note-input]').forEach((input) => {
        if (input === sourceInput) return;
        if (input.value === value) return;
        input.value = value;
      });
    },

    getLineProperty(line, key) {
      const properties = line && line.properties;
      if (!properties || !key) return '';

      if (Array.isArray(properties)) {
        const entry = properties.find((property) => property && property.name === key);
        return entry && entry.value != null ? String(entry.value) : '';
      }

      return properties[key] != null ? String(properties[key]) : '';
    },

    normalizeSummaryValue(value) {
      return String(value || '').replace(/\s+/g, ' ').trim();
    },

    truncateSummaryValue(value, maxLength = 160) {
      const normalized = this.normalizeSummaryValue(value);
      if (!normalized || normalized.length <= maxLength) return normalized;
      return `${normalized.slice(0, Math.max(0, maxLength - 3)).trim()}...`;
    },

    buildGiftSummary(cart) {
      if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) return '';

      const giftLines = [];

      cart.items.forEach((line) => {
        if (!line || this.getLineProperty(line, '_momantee_gift_service') === 'video_message') {
          return;
        }

        const services = [];
        const giftServices = this.getLineProperty(line, '_Gift Services');
        const giftMessage = this.getLineProperty(line, 'Gift note message');
        const hasGiftNote = Boolean(
          this.getLineProperty(line, 'Handwritten gift note')
          || this.normalizeSummaryValue(giftMessage)
        );
        const hasVideoMessage = Boolean(
          this.getLineProperty(line, 'Personal video message')
          || giftServices.indexOf('Personal video message') >= 0
        );

        if (hasGiftNote) services.push('Handwritten gift note');
        if (hasVideoMessage) services.push('Personal video message');
        if (!services.length) return;

        const title = this.normalizeSummaryValue(line.product_title || line.title || 'Gift item');
        const requestId = this.normalizeSummaryValue(this.getLineProperty(line, '_Gift Request ID'));
        const lines = [
          `- ${title}`,
          `  Services: ${services.join(', ')}`
        ];

        if (requestId) {
          lines.push(`  Request ID: ${requestId}`);
        }

        if (giftMessage) {
          lines.push(`  Message: ${this.truncateSummaryValue(giftMessage)}`);
        }

        giftLines.push(lines.join('\n'));
      });

      if (!giftLines.length) return '';

      return [
        this.giftSummaryStart,
        'Gift services',
        giftLines.join('\n'),
        this.giftSummaryEnd
      ].join('\n');
    },

    mergeGiftSummary(note, summary) {
      const markerPattern = /\n*\[MOMANTEE_GIFT_V1\][\s\S]*?\[\/MOMANTEE_GIFT_V1\]\n*/g;
      const cleanNote = String(note || '')
        .replace(markerPattern, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
      const cleanSummary = String(summary || '').trim();

      if (!cleanSummary) return cleanNote;
      return cleanNote ? `${cleanNote}\n\n${cleanSummary}` : cleanSummary;
    },

    async syncGiftSummaryToCart(cart) {
      if (!cart || !Array.isArray(cart.items)) return cart;

      const currentNote = typeof cart.note === 'string' ? cart.note : '';
      const nextNote = this.mergeGiftSummary(currentNote, this.buildGiftSummary(cart));

      if (nextNote === currentNote) {
        this.syncValues(nextNote);
        return cart;
      }

      try {
        const nextCart = await Hai.fetchJSON(Hai.toCartJsonUrl(Hai.routes.cartUpdate), {
          method: 'POST',
          body: { note: nextNote }
        });

        const resolvedNote = typeof nextCart.note === 'string' ? nextCart.note : nextNote;
        this.syncValues(resolvedNote);
        return nextCart || cart;
      } catch (error) {
        console.warn('Gift note summary sync failed:', error);
        return cart;
      }
    },

    setSavingState(container, isSaving) {
      const textarea = container.querySelector('[data-cart-note-input]');
      const saveButton = container.querySelector('[data-cart-note-save]');

      container.classList.toggle('is-saving', Boolean(isSaving));

      if (textarea) {
        textarea.disabled = Boolean(isSaving);
      }

      if (saveButton) {
        saveButton.disabled = Boolean(isSaving);
        if (isSaving) {
          saveButton.setAttribute('aria-busy', 'true');
        } else {
          saveButton.removeAttribute('aria-busy');
        }
      }
    },

    async save(container) {
      const textarea = container.querySelector('[data-cart-note-input]');
      if (!textarea || container.dataset.haiCartNoteSubmitting === 'true') return;

      let note = textarea.value || '';
      container.dataset.haiCartNoteSubmitting = 'true';
      this.setSavingState(container, true);

      try {
        if (Hai.cart && typeof Hai.cart.getCart === 'function') {
          const currentCart = await Hai.cart.getCart();
          note = this.mergeGiftSummary(note, this.buildGiftSummary(currentCart));
        }

        const cart = await Hai.fetchJSON(Hai.toCartJsonUrl(Hai.routes.cartUpdate), {
          method: 'POST',
          body: { note }
        });

        const nextNote = typeof cart.note === 'string' ? cart.note : note;
        this.syncValues(nextNote, textarea);

        if (Hai.cartDrawer && typeof Hai.cartDrawer.updateBadges === 'function') {
          Hai.cartDrawer.updateBadges(cart);
        }

        document.dispatchEvent(new CustomEvent('hai:cart:refresh', {
          detail: cart
        }));

        if (Hai.cartNotification && typeof Hai.cartNotification.show === 'function') {
          Hai.cartNotification.show('Order note saved');
        }
      } catch (error) {
        console.error('Cart note update error:', error);
        if (Hai.cartNotification && typeof Hai.cartNotification.show === 'function') {
          Hai.cartNotification.show('Unable to save order note');
        }
      } finally {
        delete container.dataset.haiCartNoteSubmitting;
        this.setSavingState(container, false);
      }
    }
  };

  // ===================================
  // 购物车抽屉交互  
  // ===================================

  Hai.cartDrawer = {
    init() {
      this.sectionHandle = 'cart-drawer';
      this.pendingRequest = false;
      this.queuedRemoval = null;
      this.closeTimer = null;
      this.ignoreNextCartUpdateEvent = false;

      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleDrawerClick = this.handleDrawerClick.bind(this);
      this.handleDrawerChange = this.handleDrawerChange.bind(this);
      this.handleCartUpdate = this.handleCartUpdate.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);

      this.cacheElements();

      document.addEventListener('click', this.handleDocumentClick);
      document.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('hai:cart:update', this.handleCartUpdate);

      this.bindDrawerEvents();
    },

    cacheElements() {
      this.sectionContainer = document.getElementById(`shopify-section-${this.sectionHandle}`);
      this.drawer = this.sectionContainer ? this.sectionContainer.querySelector('[data-cart-drawer]') : null;
      this.overlay = this.sectionContainer ? this.sectionContainer.querySelector('[data-cart-drawer-overlay]') : null;
      this.drawerCount = document.querySelector('[data-cart-drawer-count]');
      this.cartCountTargets = document.querySelectorAll('[data-cart-count]');
    },

    bindDrawerEvents() {
      if (this.drawer) {
        this.drawer.addEventListener('click', this.handleDrawerClick);
        this.drawer.addEventListener('change', this.handleDrawerChange);
      }

      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }
    },

    handleDocumentClick(event) {
      const toggle = event.target.closest('[data-cart-toggle]');
      if (toggle) {
        event.preventDefault();
        this.open();
        return;
      }

      const closeTrigger = event.target.closest('[data-cart-drawer-close]');
      if (closeTrigger) {
        event.preventDefault();
        this.close();
      }
    },

    handleDrawerClick(event) {
      const increase = event.target.closest('[data-cart-qty-increase]');
      const decrease = event.target.closest('[data-cart-qty-decrease]');
      const remove = event.target.closest('[data-cart-item-remove]');

      if (increase) {
        event.preventDefault();
        const line = Number(increase.dataset.line);
        const input = this.findQuantityInput(line);
        if (!input) return;
        const nextQuantity = Number(input.value || 0) + 1;
        this.updateLine(line, nextQuantity);
        return;
      }

      if (decrease) {
        event.preventDefault();
        const line = Number(decrease.dataset.line);
        const input = this.findQuantityInput(line);
        if (!input) return;
        const nextQuantity = Math.max(0, Number(input.value || 0) - 1);
        const lineItem = decrease.closest('[data-cart-item]');
        const lineKey = lineItem ? String(lineItem.dataset.lineKey || '') : '';
        this.updateLine(line, nextQuantity, { lineKey });
        return;
      }

      if (remove) {
        event.preventDefault();
        const line = Number(remove.dataset.line);
        const lineItem = remove.closest('[data-cart-item]');
        const lineKey = lineItem ? String(lineItem.dataset.lineKey || '') : '';
        this.updateLine(line, 0, { lineKey });
      }
    },

    handleDrawerChange(event) {
      if (!event.target.matches('[data-cart-qty-input]')) return;
      const line = Number(event.target.dataset.line);
      const quantity = Math.max(0, Number(event.target.value || 0));
      const lineItem = event.target.closest('[data-cart-item]');
      const lineKey = lineItem ? String(lineItem.dataset.lineKey || '') : '';
      this.updateLine(line, quantity, { lineKey });
    },

    handleCartUpdate(event) {
      if (this.ignoreNextCartUpdateEvent) {
        this.ignoreNextCartUpdateEvent = false;
        return;
      }

      const cart = event.detail;
      if (!cart) return;
      (async () => {
        let nextCart = cart;
        if (Hai.addonCartSync && typeof Hai.addonCartSync.enforce === 'function') {
          nextCart = await Hai.addonCartSync.enforce(cart, { notify: false });
        }
        await this.applyCartState(nextCart);
      })().catch((error) => {
        console.error('Failed to sync cart state:', error);
      });
    },

    handleKeydown(event) {
      if (event.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    },

    findQuantityInput(line) {
      if (!this.drawer) return null;
      return this.drawer.querySelector(`[data-cart-qty-input][data-line="${line}"]`);
    },

    isOpen() {
      return this.drawer && !this.drawer.hasAttribute('hidden');
    },

    open() {
      if (!this.drawer) return;

      this.clearCloseTimer();
      this.drawer.hidden = false;
      requestAnimationFrame(() => {
        this.drawer.classList.add('active');
      });

      if (this.overlay) {
        this.overlay.hidden = false;
        requestAnimationFrame(() => {
          this.overlay.classList.add('active');
        });
      }

      document.body.style.overflow = 'hidden';
      document.documentElement.classList.add('hai-cart-open');
    },

    close() {
      if (!this.drawer) return;

      this.clearCloseTimer();
      this.drawer.classList.remove('active');
      if (this.overlay) {
        this.overlay.classList.remove('active');
      }

      this.closeTimer = window.setTimeout(() => {
        if (this.drawer) {
          this.drawer.hidden = true;
        }
        if (this.overlay) {
          this.overlay.hidden = true;
        }
        this.closeTimer = null;
      }, 200);

      document.body.style.overflow = '';
      document.documentElement.classList.remove('hai-cart-open');
    },

    restoreOpenState() {
      if (!this.drawer) return;

      this.clearCloseTimer();
      this.drawer.hidden = false;
      this.drawer.style.transition = 'none';
      void this.drawer.offsetHeight;
      this.drawer.classList.add('active');

      if (this.overlay) {
        this.overlay.hidden = false;
        this.overlay.style.transition = 'none';
        void this.overlay.offsetHeight;
        this.overlay.classList.add('active');
      }

      document.body.style.overflow = 'hidden';
      document.documentElement.classList.add('hai-cart-open');

      requestAnimationFrame(() => {
        if (this.drawer) {
          this.drawer.style.removeProperty('transition');
        }

        if (this.overlay) {
          this.overlay.style.removeProperty('transition');
        }
      });
    },

    clearCloseTimer() {
      if (!this.closeTimer) return;
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    },

    async updateLine(line, quantity, options = {}) {
      const lineKey = String(options.lineKey || '').trim();

      if (this.pendingRequest) {
        if (quantity === 0 && lineKey) {
          this.queuedRemoval = { line, quantity, lineKey };
        }
        return;
      }

      this.pendingRequest = true;
      this.ignoreNextCartUpdateEvent = true;

      try {
        let cart;
        if (quantity === 0 && lineKey && typeof Hai.cart.removeItemWithRelated === 'function') {
          cart = await Hai.cart.removeItemWithRelated(lineKey);
        } else {
          cart = await Hai.cart.updateItem(line, quantity);
        }
        if (Hai.addonCartSync && typeof Hai.addonCartSync.enforce === 'function') {
          cart = await Hai.addonCartSync.enforce(cart, { notify: true });
        }
        if (Hai.cartNote && typeof Hai.cartNote.syncGiftSummaryToCart === 'function') {
          cart = await Hai.cartNote.syncGiftSummaryToCart(cart);
        }
        await this.applyCartState(cart);

        const message = quantity === 0
          ? (Hai.strings.cartRemoved || 'Removed from cart')
          : (Hai.strings.cartUpdated || 'Cart updated');

        Hai.cartNotification.show(message);
      } catch (error) {
        console.error('Update cart error:', error);
        Hai.cartNotification.show(Hai.strings.cartError || 'Cart update failed');
      } finally {
        this.ignoreNextCartUpdateEvent = false;
        this.pendingRequest = false;
        const queuedRemoval = this.queuedRemoval;
        this.queuedRemoval = null;

        if (queuedRemoval) {
          this.updateLine(queuedRemoval.line, queuedRemoval.quantity, {
            lineKey: queuedRemoval.lineKey
          });
        }
      }
    },

    async applyCartState(cart) {
      if (!cart) return;

      this.updateBadges(cart);
      await this.renderSections(['cart-drawer'], {
        expectedCart: cart
      });
      this.updateBadges(cart);

      document.dispatchEvent(new CustomEvent('hai:cart:refresh', {
        detail: cart
      }));
    },

    updateBadges(cart) {
      const itemCount = Math.max(0, Number(cart.item_count) || 0);
      this.cartCountTargets = document.querySelectorAll('[data-cart-count]');

      if (this.drawerCount) {
        this.drawerCount.textContent = String(itemCount);
      }

      this.cartCountTargets.forEach((target) => {
        if (!target) return;
        target.textContent = itemCount > 0 ? String(itemCount) : '';
        target.hidden = itemCount <= 0;
      });
    },

    hasRenderedCartCountMismatch(expectedCart, sectionHandles) {
      if (
        !expectedCart
        || !Array.isArray(sectionHandles)
        || !sectionHandles.includes('cart-drawer')
        || !this.drawerCount
      ) {
        return false;
      }

      const expectedCount = Math.max(0, Number(expectedCart.item_count) || 0);
      const renderedText = String(this.drawerCount.textContent || '').trim();
      const renderedCount = renderedText === '' ? 0 : Number(renderedText);

      if (!Number.isFinite(renderedCount)) {
        return true;
      }

      return renderedCount !== expectedCount;
    },

    async renderSections(sectionHandles, options = {}) {
      if (!Array.isArray(sectionHandles) || sectionHandles.length === 0) {
        return true;
      }

      const expectedCart = options.expectedCart || null;
      const retryCount = Math.max(0, Number(options.retryCount) || 0);

      const params = new URLSearchParams({
        sections: sectionHandles.join(','),
        sections_url: window.location.pathname
      });
      const url = `${Hai.routes.cart}?${params.toString()}`;

      try {
        const response = await fetch(url, {
          cache: 'no-store',
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (!response.ok) {
          throw new Error(`Section render failed: ${response.status}`);
        }

        const html = await response.json();
        const wasOpen = this.isOpen();

        sectionHandles.forEach((handle) => {
          const container = document.getElementById(`shopify-section-${handle}`);
          if (container && html[handle]) {
            container.innerHTML = html[handle];
          }
        });

        this.cacheElements();
        this.bindDrawerEvents();
        if (Hai.mainCart && typeof Hai.mainCart.init === 'function') {
          Hai.mainCart.init();
        }

        if (wasOpen) {
          this.restoreOpenState();
        }

        if (this.hasRenderedCartCountMismatch(expectedCart, sectionHandles)) {
          if (retryCount >= 2) {
            return true;
          }

          await new Promise((resolve) => {
            window.setTimeout(resolve, 150 * (retryCount + 1));
          });

          return this.renderSections(sectionHandles, {
            expectedCart,
            retryCount: retryCount + 1
          });
        }

        return true;
      } catch (error) {
        console.error('Failed to refresh cart section:', error);
        return false;
      }
    }
  };

  Hai.mainCart = {
    init() {
      this.form = document.querySelector('[data-cart-page] [data-cart-form]');
      if (!this.form || this.form.dataset.haiMainCartBound === 'true') return;

      this.form.dataset.haiMainCartBound = 'true';
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.form.addEventListener('submit', this.handleSubmit);
      this.form.addEventListener('click', this.handleClick);
      this.form.addEventListener('change', this.handleChange);
    },

    handleSubmit(event) {
      if (!this.form) return;
      if (this.form.dataset.haiMainCartSubmitting === 'true') {
        event.preventDefault();
        return;
      }

      const submitter = event.submitter || null;
      const isCheckout = Boolean(submitter && submitter.name === 'checkout');
      if (!isCheckout) {
        event.preventDefault();
        return;
      }
    },

    handleClick(event) {
      const increase = event.target.closest('[data-cart-qty-increase]');
      const decrease = event.target.closest('[data-cart-qty-decrease]');
      const remove = event.target.closest('[data-cart-remove]');

      if (increase) {
        event.preventDefault();
        const line = Number(increase.dataset.line || 0);
        const input = this.findQuantityInput(line);
        if (!input) return;
        const currentQuantity = Math.max(0, Number(input.value || 0));
        const nextQuantity = currentQuantity + 1;
        input.value = nextQuantity;
        this.updateLine(line, nextQuantity);
        return;
      }

      if (decrease) {
        event.preventDefault();
        const line = Number(decrease.dataset.line || 0);
        const input = this.findQuantityInput(line);
        if (!input) return;
        const currentQuantity = Math.max(0, Number(input.value || 0));
        const nextQuantity = Math.max(0, currentQuantity - 1);
        input.value = nextQuantity;
        const lineItem = decrease.closest('[data-cart-item]');
        const lineKey = lineItem ? String(lineItem.dataset.lineKey || '') : '';
        this.updateLine(line, nextQuantity, { lineKey });
        return;
      }

      if (remove) {
        event.preventDefault();
        const line = Number(remove.dataset.line || 0);
        const lineItem = remove.closest('[data-cart-item]');
        const lineKey = lineItem ? String(lineItem.dataset.lineKey || '') : '';
        this.updateLine(line, 0, { lineKey });
      }
    },

    handleChange(event) {
      if (!event.target.matches('[data-cart-qty-input]')) return;
      const line = Number(event.target.dataset.line || 0);
      const quantity = Math.max(0, Number(event.target.value || 0));
      event.target.value = quantity;
      const lineItem = event.target.closest('[data-cart-item]');
      const lineKey = lineItem ? String(lineItem.dataset.lineKey || '') : '';
      this.updateLine(line, quantity, { lineKey });
    },

    findQuantityInput(line) {
      if (!this.form || !line) return null;
      return this.form.querySelector(`[data-cart-qty-input][data-line="${line}"]`);
    },

    findLineRow(line) {
      if (!this.form || !line) return null;
      return this.form.querySelector(`[data-cart-item][data-line="${line}"]`);
    },

    toggleLineUpdating(line, isUpdating) {
      const row = this.findLineRow(line);
      if (!row) return;
      row.classList.toggle('is-updating', Boolean(isUpdating));
    },

    collectUpdates() {
      if (!this.form) return {};
      const quantityInputs = Array.from(this.form.querySelectorAll('[data-cart-quantity]'));
      const updates = {};

      quantityInputs.forEach((input, index) => {
        const quantity = Math.max(0, Number(input.value || 0));
        const line = Math.max(0, Number(input.dataset.line || (index + 1)));
        if (line <= 0) return;
        updates[String(line)] = quantity;
      });

      return updates;
    },

    calculateSavings(cart) {
      if (!cart || !Array.isArray(cart.items)) return 0;

      return cart.items.reduce((total, item) => {
        const original = Number(item.original_line_price || 0);
        const final = Number(item.final_line_price || 0);
        if (original > final) {
          return total + (original - final);
        }

        return total;
      }, 0);
    },

    patchCartDom(cart, options = {}) {
      if (!this.form || !cart) return;
      const skipLineItems = options.skipLineItems === true;

      const subtotal = this.form.querySelector('[data-cart-subtotal]');
      if (subtotal) {
        subtotal.textContent = Hai.formatMoney(cart.total_price || 0);
      }

      const checkoutPrice = this.form.querySelector('[data-cart-checkout-price]');
      if (checkoutPrice) {
        checkoutPrice.textContent = Hai.formatMoney(cart.total_price || 0);
      }

      const savingsRow = this.form.querySelector('[data-cart-savings-row]');
      const savingsValue = this.form.querySelector('[data-cart-total-savings]');
      const savings = this.calculateSavings(cart);

      if (savingsRow) {
        savingsRow.hidden = savings <= 0;
      }

      if (savingsValue) {
        savingsValue.textContent = Hai.formatMoney(savings);
      }

      if (skipLineItems || !Array.isArray(cart.items)) return;

      cart.items.forEach((item, index) => {
        const line = index + 1;
        const currentPrice = this.form.querySelector(`[data-cart-line-current][data-line="${line}"]`);
        const originalPrice = this.form.querySelector(`[data-cart-line-original][data-line="${line}"]`);
        const quantityInput = this.findQuantityInput(line);

        if (currentPrice) {
          currentPrice.textContent = Hai.formatMoney(item.final_line_price || 0);
        }

        if (originalPrice) {
          const shouldShowOriginal = Number(item.original_line_price || 0) > Number(item.final_line_price || 0);
          originalPrice.textContent = Hai.formatMoney(item.original_line_price || 0);
          originalPrice.hidden = !shouldShowOriginal;
        }

        if (quantityInput) {
          quantityInput.value = Math.max(0, Number(item.quantity || 0));
        }
      });
    },

    async syncForm({ redirectToCheckout = false } = {}) {
      if (!this.form || this.form.dataset.haiMainCartSubmitting === 'true') return;
      this.form.dataset.haiMainCartSubmitting = 'true';

      try {
        const updates = this.collectUpdates();
        let cart = await Hai.fetchJSON(Hai.toCartJsonUrl(Hai.routes.cartUpdate), {
          method: 'POST',
          body: { updates }
        });

        if (Hai.addonCartSync && typeof Hai.addonCartSync.enforce === 'function') {
          cart = await Hai.addonCartSync.enforce(cart, { notify: true });
        }
        if (Hai.cartNote && typeof Hai.cartNote.syncGiftSummaryToCart === 'function') {
          cart = await Hai.cartNote.syncGiftSummaryToCart(cart);
        }

        if (redirectToCheckout) {
          window.location.assign('/checkout');
          return;
        }

        await this.applyCartState(cart);
        if (Hai.cartNotification && typeof Hai.cartNotification.show === 'function') {
          Hai.cartNotification.show(Hai.strings.cartUpdated || 'Cart updated');
        }
      } catch (error) {
        console.error('Main cart sync error:', error);
        if (Hai.cartNotification && typeof Hai.cartNotification.show === 'function') {
          Hai.cartNotification.show(Hai.strings.cartError || 'Cart update failed');
        }
      } finally {
        if (this.form) {
          delete this.form.dataset.haiMainCartSubmitting;
        }
      }
    },

    async updateLine(line, quantity, options = {}) {
      if (!this.form || !line || this.form.dataset.haiMainCartSubmitting === 'true') return;

      const input = this.findQuantityInput(line);
      if (input && Math.max(0, Number(input.value || 0)) !== quantity) {
        input.value = quantity;
      }

      this.form.dataset.haiMainCartSubmitting = 'true';
      this.toggleLineUpdating(line, true);

      try {
        const lineKey = String(options.lineKey || '').trim();
        let cart;

        if (quantity === 0 && lineKey && typeof Hai.cart.removeItemWithRelated === 'function') {
          cart = await Hai.cart.removeItemWithRelated(lineKey);
        } else {
          cart = await Hai.fetchJSON(Hai.toCartJsonUrl(Hai.routes.cartChange), {
            method: 'POST',
            body: {
              line: line,
              quantity: quantity
            }
          });
        }

        if (Hai.addonCartSync && typeof Hai.addonCartSync.enforce === 'function') {
          cart = await Hai.addonCartSync.enforce(cart, { notify: true });
        }
        if (Hai.cartNote && typeof Hai.cartNote.syncGiftSummaryToCart === 'function') {
          cart = await Hai.cartNote.syncGiftSummaryToCart(cart);
        }

        await this.applyCartState(cart, {
          skipLineItems: quantity === 0,
          forceReloadOnRenderFailure: quantity === 0
        });

        if (Hai.cartNotification && typeof Hai.cartNotification.show === 'function') {
          const message = quantity === 0
            ? (Hai.strings.cartRemoved || 'Removed from cart')
            : (Hai.strings.cartUpdated || 'Cart updated');
          Hai.cartNotification.show(message);
        }
      } catch (error) {
        console.error('Main cart line update error:', error);
        if (Hai.cartNotification && typeof Hai.cartNotification.show === 'function') {
          Hai.cartNotification.show(Hai.strings.cartError || 'Cart update failed');
        }
      } finally {
        this.toggleLineUpdating(line, false);
        if (this.form) {
          delete this.form.dataset.haiMainCartSubmitting;
        }
      }
    },

    async applyCartState(cart, options = {}) {
      if (!cart) return;

      this.patchCartDom(cart, options);

      if (Hai.cartDrawer && typeof Hai.cartDrawer.updateBadges === 'function') {
        Hai.cartDrawer.updateBadges(cart);
      }

      let renderSucceeded = true;
      if (Hai.cartDrawer && typeof Hai.cartDrawer.renderSections === 'function') {
        renderSucceeded = await Hai.cartDrawer.renderSections(['main-cart', 'cart-drawer'], {
          expectedCart: cart
        });
      }

      if (renderSucceeded === false && options.forceReloadOnRenderFailure) {
        window.location.assign(Hai.routes.cart);
        return;
      }

      document.dispatchEvent(new CustomEvent('hai:cart:refresh', {
        detail: cart
      }));
    }
  };

  // ===================================
  // 快速加入购物车
  // ===================================

  Hai.countdown = {
    parseTargetTimestamp(rawValue) {
      const normalizedValue = String(rawValue || '').trim();
      if (!normalizedValue) return null;

      if (normalizedValue === 'today-end' || normalizedValue === 'today-end-local') {
        const endOfDay = new Date();
        endOfDay.setHours(23, 59, 59, 999);
        return endOfDay.getTime();
      }

      if (/^\d+$/.test(normalizedValue)) {
        const numericValue = Number(normalizedValue);
        if (!Number.isFinite(numericValue) || numericValue <= 0) return null;

        return normalizedValue.length >= 13 ? numericValue : numericValue * 1000;
      }

      if (normalizedValue.includes('T')) {
        const isoParsed = new Date(normalizedValue).getTime();
        return Number.isFinite(isoParsed) ? isoParsed : null;
      }

      const dateTimeMatch = normalizedValue.match(
        /^(\d{4})[-\/](\d{1,2})[-\/](\d{1,2})(?:[ T]+(\d{1,2})(?::(\d{1,2}))?(?::(\d{1,2}))?)?$/
      );

      if (dateTimeMatch) {
        const year = Number(dateTimeMatch[1]);
        const month = Number(dateTimeMatch[2]) - 1;
        const day = Number(dateTimeMatch[3]);
        const hours = Number(dateTimeMatch[4] || 23);
        const minutes = Number(dateTimeMatch[5] || 59);
        const seconds = Number(dateTimeMatch[6] || 59);
        const parsed = new Date(year, month, day, hours, minutes, seconds).getTime();

        return Number.isFinite(parsed) ? parsed : null;
      }

      const fallbackParsed = new Date(normalizedValue).getTime();
      return Number.isFinite(fallbackParsed) ? fallbackParsed : null;
    },

    getRemainingParts(targetTimestamp) {
      const diff = Number(targetTimestamp) - Date.now();
      if (!Number.isFinite(diff) || diff <= 0) return null;

      const totalSeconds = Math.floor(diff / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      return {
        days,
        hours,
        minutes,
        seconds,
        paddedDays: String(days).padStart(2, '0'),
        paddedHours: String(hours).padStart(2, '0'),
        paddedMinutes: String(minutes).padStart(2, '0'),
        paddedSeconds: String(seconds).padStart(2, '0')
      };
    }
  };

  Hai.productCardCountdown = {
    selector: '[data-product-card-countdown], [data-hai-countdown]',

    parseTargetTimestamp(rawValue) {
      return Hai.countdown.parseTargetTimestamp(rawValue);
    },

    init() {
      if (!this.timers) {
        this.timers = new WeakMap();
      }

      if (!this.lifecycleBound) {
        this.lifecycleBound = true;
        this.handleCollectionUpdated = this.handleCollectionUpdated.bind(this);
        this.handleSectionLoad = this.handleSectionLoad.bind(this);

        document.addEventListener('hai:collection:updated', this.handleCollectionUpdated);
        document.addEventListener('shopify:section:load', this.handleSectionLoad);
      }

      document.querySelectorAll(this.selector).forEach((countdownEl) => {
        this.bindCountdown(countdownEl);
      });
    },

    handleCollectionUpdated() {
      this.init();
    },

    handleSectionLoad(event) {
      if (!event || !event.target || typeof event.target.querySelectorAll !== 'function') return;

      event.target.querySelectorAll(this.selector).forEach((countdownEl) => {
        this.bindCountdown(countdownEl);
      });
    },

    bindCountdown(countdownEl) {
      if (!countdownEl || countdownEl.dataset.haiProductCardCountdownBound === 'true') return;

      const targetTimestamp = Hai.countdown.parseTargetTimestamp(countdownEl.dataset.countdownTarget);
      if (!Number.isFinite(targetTimestamp) || targetTimestamp <= 0) {
        countdownEl.hidden = true;
        return;
      }

      const textEl = countdownEl.querySelector('[data-product-card-countdown-text], [data-countdown-text]');
      const daysEl = countdownEl.querySelector('[data-countdown-days]');
      const hoursEl = countdownEl.querySelector('[data-countdown-hours]');
      const minutesEl = countdownEl.querySelector('[data-countdown-minutes]');
      const secondsEl = countdownEl.querySelector('[data-countdown-seconds]');

      if (!textEl && !daysEl && !hoursEl && !minutesEl && !secondsEl) {
        countdownEl.hidden = true;
        return;
      }

      countdownEl.dataset.haiProductCardCountdownBound = 'true';

      const destroy = () => {
        const timer = this.timers.get(countdownEl);
        if (timer && timer.id) {
          window.clearInterval(timer.id);
        }

        this.timers.delete(countdownEl);
        delete countdownEl.dataset.haiProductCardCountdownBound;
      };

      const render = () => {
        if (!countdownEl.isConnected) {
          destroy();
          return;
        }

        const remaining = Hai.countdown.getRemainingParts(targetTimestamp);
        if (!remaining) {
          countdownEl.hidden = true;
          destroy();
          return;
        }

        const {
          days,
          hours,
          minutes,
          seconds,
          paddedDays,
          paddedHours,
          paddedMinutes,
          paddedSeconds
        } = remaining;

        if (textEl) {
          textEl.textContent = `${days}d : ${hours}h : ${minutes}m : ${seconds}s`;
        }

        if (daysEl) daysEl.textContent = paddedDays;
        if (hoursEl) hoursEl.textContent = paddedHours;
        if (minutesEl) minutesEl.textContent = paddedMinutes;
        if (secondsEl) secondsEl.textContent = paddedSeconds;

        countdownEl.hidden = false;
      };

      render();

      if (countdownEl.hidden) {
        return;
      }

      const timerId = window.setInterval(render, 1000);
      this.timers.set(countdownEl, { id: timerId });
    }
  };

  Hai.flipCountdown = {
    selector: '[data-hai-flip-countdown]',

    init() {
      if (!this.timers) {
        this.timers = new WeakMap();
      }

      if (!this.lifecycleBound) {
        this.lifecycleBound = true;
        this.handleCollectionUpdated = this.handleCollectionUpdated.bind(this);
        this.handleSectionLoad = this.handleSectionLoad.bind(this);

        document.addEventListener('hai:collection:updated', this.handleCollectionUpdated);
        document.addEventListener('shopify:section:load', this.handleSectionLoad);
      }

      document.querySelectorAll(this.selector).forEach((countdownEl) => {
        this.bindCountdown(countdownEl);
      });
    },

    handleCollectionUpdated() {
      this.init();
    },

    handleSectionLoad(event) {
      if (!event || !event.target || typeof event.target.querySelectorAll !== 'function') return;

      event.target.querySelectorAll(this.selector).forEach((countdownEl) => {
        this.bindCountdown(countdownEl);
      });
    },

    bindCountdown(countdownEl) {
      if (!countdownEl || countdownEl.dataset.haiFlipCountdownBound === 'true') return;

      const targetTimestamp = Hai.countdown.parseTargetTimestamp(countdownEl.dataset.countdownTarget);
      if (!Number.isFinite(targetTimestamp) || targetTimestamp <= 0) {
        countdownEl.hidden = true;
        return;
      }

      const groups = {
        days: countdownEl.querySelector('[data-flip-countdown-group="days"]'),
        hours: countdownEl.querySelector('[data-flip-countdown-group="hours"]'),
        minutes: countdownEl.querySelector('[data-flip-countdown-group="minutes"]'),
        seconds: countdownEl.querySelector('[data-flip-countdown-group="seconds"]')
      };

      if (!groups.days && !groups.hours && !groups.minutes && !groups.seconds) {
        countdownEl.hidden = true;
        return;
      }

      const expiredAction = String(countdownEl.dataset.countdownExpiredAction || 'hide').trim().toLowerCase();
      countdownEl.dataset.haiFlipCountdownBound = 'true';

      const destroy = () => {
        const timer = this.timers.get(countdownEl);
        if (timer && timer.id) {
          window.clearInterval(timer.id);
        }

        this.timers.delete(countdownEl);
        delete countdownEl.dataset.haiFlipCountdownBound;
      };

      const render = () => {
        if (!countdownEl.isConnected) {
          destroy();
          return false;
        }

        const remaining = Hai.countdown.getRemainingParts(targetTimestamp);
        if (!remaining) {
          this.handleExpiry(countdownEl, expiredAction);

          if (expiredAction === 'zero') {
            this.updateGroup(groups.days, 0);
            this.updateGroup(groups.hours, 0);
            this.updateGroup(groups.minutes, 0);
            this.updateGroup(groups.seconds, 0);
            countdownEl.hidden = false;
          } else {
            countdownEl.hidden = true;
          }

          destroy();
          return false;
        }

        this.updateGroup(groups.days, remaining.days);
        this.updateGroup(groups.hours, remaining.hours);
        this.updateGroup(groups.minutes, remaining.minutes);
        this.updateGroup(groups.seconds, remaining.seconds);
        countdownEl.hidden = false;
        return true;
      };

      if (!render()) {
        return;
      }

      const timerId = window.setInterval(() => {
        render();
      }, 1000);

      this.timers.set(countdownEl, { id: timerId });
    },

    handleExpiry(countdownEl, expiredAction) {
      if (!countdownEl || countdownEl.dataset.haiFlipCountdownExpired === 'true') return;

      countdownEl.dataset.haiFlipCountdownExpired = 'true';

      const shell = countdownEl.closest('[data-flip-countdown-shell]');
      if (shell && shell.dataset.replaceOnExpiry === 'true') {
        const liveContent = shell.querySelector('[data-flip-countdown-live]');
        const expiredContent = shell.querySelector('[data-flip-countdown-expired]');

        if (liveContent) {
          liveContent.hidden = true;
        }

        if (expiredContent) {
          expiredContent.hidden = false;
        }
      }

      countdownEl.dispatchEvent(new CustomEvent('hai:flip-countdown:expired', {
        bubbles: true,
        detail: {
          action: expiredAction
        }
      }));
    },

    updateGroup(groupEl, value) {
      if (!groupEl) return;

      const cards = Array.from(groupEl.querySelectorAll('[data-flip-card]'));
      if (!cards.length) return;

      const digits = String(value).padStart(cards.length, '0').slice(-cards.length);
      cards.forEach((card, index) => {
        this.triggerFlip(card, digits[index] || '0');
      });
    },

    triggerFlip(card, nextValue) {
      if (!card) return;

      const topStatic = card.querySelector('[data-flip-face="top"]');
      const bottomStatic = card.querySelector('[data-flip-face="bottom"]');
      const topFlip = card.querySelector('[data-flip-face="top-flip"]');
      const bottomFlip = card.querySelector('[data-flip-face="bottom-flip"]');

      if (!topStatic || !bottomStatic || !topFlip || !bottomFlip) return;

      const currentValue = String(topStatic.textContent || '').trim() || '0';
      const normalizedNextValue = String(nextValue || '0');

      if (normalizedNextValue === currentValue) return;

      topFlip.textContent = currentValue;
      bottomFlip.textContent = normalizedNextValue;

      topFlip.classList.remove('hidden', 'is-flipping-top');
      bottomFlip.classList.remove('hidden', 'is-flipping-bottom');

      topStatic.textContent = normalizedNextValue;
      bottomStatic.textContent = currentValue;

      void topFlip.offsetWidth;
      topFlip.classList.add('is-flipping-top');
      bottomFlip.classList.add('is-flipping-bottom');

      bottomFlip.addEventListener('animationend', () => {
        bottomStatic.textContent = normalizedNextValue;
        topFlip.classList.add('hidden');
        bottomFlip.classList.add('hidden');
        topFlip.classList.remove('is-flipping-top');
        bottomFlip.classList.remove('is-flipping-bottom');
      }, { once: true });
    }
  };

  Hai.productCard = {
    previewSlideDuration: 560,
    previewAutoplayDelay: 1600,

    init() {
      if (!this.cardStates) {
        this.cardStates = new WeakMap();
      }

      if (!this.initialized) {
        this.initialized = true;
        this.handleClick = this.handleClick.bind(this);
        this.handleCollectionUpdated = this.handleCollectionUpdated.bind(this);
        this.handleSectionLoad = this.handleSectionLoad.bind(this);

        document.addEventListener('click', this.handleClick);
        document.addEventListener('hai:collection:updated', this.handleCollectionUpdated);
        document.addEventListener('shopify:section:load', this.handleSectionLoad);
      }

      this.initCards(document);
    },

    handleCollectionUpdated() {
      this.initCards(document);
    },

    handleSectionLoad(event) {
      if (!event || !event.target) return;
      this.initCards(event.target);
    },

    initCards(root) {
      const target = root || document;
      const cards = [];

      if (typeof target.matches === 'function' && target.matches('[data-product-card]')) {
        cards.push(target);
      }

      if (typeof target.querySelectorAll === 'function') {
        target.querySelectorAll('[data-product-card]').forEach((card) => {
          cards.push(card);
        });
      }

        cards.forEach((card) => {
        this.bindCardPreview(card);
      });
    },

      bindCardPreview(card) {
        if (!card || card.dataset.productCardPreviewEnabled !== 'true') {
          return;
        }

        const media = card.querySelector('[data-product-card-media]');
        const previewSwatches = this.getPreviewSwatches(card);
        const hoverImage = card.querySelector('[data-product-card-hover-image]');
        if (!media || !hoverImage || previewSwatches.length < 3) {
          this.syncPreviewDotsWithActive(card);
          return;
        }

        if (card.dataset.haiProductCardPreviewBound === 'true') {
          const state = this.cardStates.get(card);
          if (state) {
            state.previewSwatches = previewSwatches;
            state.currentPreviewIndex = this.getCardDefaultIndex(card, previewSwatches);
          }
          this.syncPreviewDotsWithActive(card);
          this.resetPreviewLayers(card);
          return;
        }

        const primaryImage = card.querySelector('[data-product-card-primary-image]');
        const defaultImage = primaryImage
          ? this.getImageDataFromImage(primaryImage)
          : null;

          const state = {
            media,
            previewSwatches,
            defaultImage,
            currentPreviewIndex: this.getCardDefaultIndex(card, previewSwatches),
            hovered: false,
            autoplayTimer: null,
            previewAnimationTimer: null,
            isAnimating: false,
            previewDirection: 'left',
            lastPointerX: null
          };

          state.handleMouseEnter = (event) => {
            const pointerX = event && Number.isFinite(event.clientX) ? event.clientX : null;
            state.lastPointerX = pointerX;
            this.startPreviewSlideshow(card);
          };

          state.handleMouseMove = (event) => {
            this.updatePreviewDirection(card, event);
          };

          state.handleMouseLeave = () => {
            this.stopPreviewSlideshow(card);
          };

          media.addEventListener('mouseenter', state.handleMouseEnter);
          media.addEventListener('mousemove', state.handleMouseMove);
          media.addEventListener('mouseleave', state.handleMouseLeave);

        this.cardStates.set(card, state);
        card.dataset.haiProductCardPreviewBound = 'true';
        this.syncPreviewDotsWithActive(card);
        this.resetPreviewLayers(card);
      },

    getPreviewSwatches(card) {
      if (!card) return [];
      return Array.from(card.querySelectorAll('[data-product-card-swatch]')).slice(0, 3);
    },

    getPreviewDots(card) {
      if (!card) return [];
      return Array.from(card.querySelectorAll('[data-product-card-preview-dot]'));
    },

    getCardDefaultIndex(card, previewSwatches) {
      if (!card || card.dataset.haiProductCardUserSelectedSwatch !== 'true') {
        return 0;
      }

      const swatches = Array.isArray(previewSwatches) && previewSwatches.length
        ? previewSwatches
        : this.getPreviewSwatches(card);

      const activeSwatch = card.querySelector('[data-product-card-swatch].is-active');
      if (!activeSwatch) return 0;

      const matchedIndex = swatches.findIndex((swatch) => swatch === activeSwatch);
      if (matchedIndex >= 0) return matchedIndex;

      return 0;
    },

      getImageDataFromImage(image) {
        if (!image) return null;

        return {
          src: image.getAttribute('src') || '',
          alt: image.getAttribute('alt') || '',
          width: Number(image.getAttribute('width') || 0),
          height: Number(image.getAttribute('height') || 0)
        };
      },

      updatePreviewDots(card, activeIndex) {
        this.getPreviewDots(card).forEach((dot, index) => {
          dot.classList.toggle('is-active', index === activeIndex);
      });
    },

    syncPreviewDotsWithActive(card) {
      if (!card || card.dataset.productCardPreviewEnabled !== 'true') return;

      const state = this.cardStates.get(card);
      const previewSwatches = state && Array.isArray(state.previewSwatches)
        ? state.previewSwatches
        : this.getPreviewSwatches(card);
      const defaultIndex = this.getCardDefaultIndex(card, previewSwatches);

      if (state) {
        state.currentPreviewIndex = defaultIndex;
      }

        this.updatePreviewDots(card, defaultIndex);
      },

      resetPreviewLayers(card) {
        const primaryImage = card ? card.querySelector('[data-product-card-primary-image]') : null;
        const hoverImage = card ? card.querySelector('[data-product-card-hover-image]') : null;
        if (!primaryImage || !hoverImage) return;

        primaryImage.style.transition = 'none';
        hoverImage.style.transition = 'none';
        primaryImage.style.transform = 'translateX(0)';
        hoverImage.style.transform = 'translateX(100%)';
      },

      clearPreviewTimers(state) {
        if (!state) return;

        if (state.autoplayTimer) {
          window.clearInterval(state.autoplayTimer);
          state.autoplayTimer = null;
        }

        if (state.previewAnimationTimer) {
          window.clearTimeout(state.previewAnimationTimer);
          state.previewAnimationTimer = null;
        }

        state.isAnimating = false;
      },

      getDirectionalPreviewIndex(currentIndex, total, direction) {
        if (!Number.isFinite(total) || total <= 0) return 0;

        if (direction === 'right') {
          return (currentIndex - 1 + total) % total;
        }

        return (currentIndex + 1) % total;
      },

      updatePreviewDirection(card, event) {
        const state = this.cardStates.get(card);
        if (!state || !state.hovered || !event) return;

        const pointerX = Number(event.clientX);
        if (!Number.isFinite(pointerX)) return;

        if (!Number.isFinite(state.lastPointerX)) {
          state.lastPointerX = pointerX;
          return;
        }

        const deltaX = pointerX - state.lastPointerX;
        state.lastPointerX = pointerX;

        if (Math.abs(deltaX) < 4) return;

        state.previewDirection = deltaX > 0 ? 'right' : 'left';
      },

      startPreviewSlideshow(card) {
        const state = this.cardStates.get(card);
        if (!state || !Array.isArray(state.previewSwatches) || state.previewSwatches.length < 3) return;
        if (state.hovered) return;

        state.hovered = true;
        state.previewDirection = 'left';
        state.currentPreviewIndex = this.getCardDefaultIndex(card, state.previewSwatches);
        card.classList.add('is-preview-hovered');

        this.clearPreviewTimers(state);
        this.finishPreviewAnimation(card);

        const nextIndex = this.getDirectionalPreviewIndex(
          state.currentPreviewIndex,
          state.previewSwatches.length,
          state.previewDirection
        );
        this.setPreviewIndex(card, nextIndex, state.previewDirection);

        state.autoplayTimer = window.setInterval(() => {
          if (!state.hovered || state.isAnimating) return;

          const followingIndex = this.getDirectionalPreviewIndex(
            state.currentPreviewIndex,
            state.previewSwatches.length,
            state.previewDirection
          );
          this.setPreviewIndex(card, followingIndex, state.previewDirection);
        }, this.previewAutoplayDelay);
      },

      stopPreviewSlideshow(card) {
        const state = this.cardStates.get(card);
        if (!state) return;

        state.hovered = false;
        state.lastPointerX = null;
        this.clearPreviewTimers(state);
        this.finishPreviewAnimation(card);
        card.classList.remove('is-preview-hovered');
        this.restoreCardImage(card);
      },

      setPreviewIndex(card, index, movement) {
        const state = this.cardStates.get(card);
        if (!state || !Array.isArray(state.previewSwatches) || !state.previewSwatches.length || state.isAnimating) return;

        const safeIndex = Math.max(0, Math.min(state.previewSwatches.length - 1, Number(index) || 0));
        if (state.currentPreviewIndex === safeIndex) return;

        const previewSwatch = state.previewSwatches[safeIndex];
        if (!previewSwatch) return;

        state.currentPreviewIndex = safeIndex;
        this.updatePreviewDots(card, safeIndex);
        this.animatePreviewChange(card, previewSwatch, movement);
      },

      restoreCardImage(card) {
        const state = this.cardStates.get(card);
        if (!card) return;

        this.finishPreviewAnimation(card);

        const activeSwatch = card.querySelector('[data-product-card-swatch].is-active');
        if (card.dataset.haiProductCardUserSelectedSwatch === 'true' && activeSwatch) {
          this.updateCardImages(card, activeSwatch);
          this.syncPreviewDotsWithActive(card);
          this.resetPreviewLayers(card);
          return;
        }

        if (!state || !state.defaultImage || !state.defaultImage.src) {
          this.updatePreviewDots(card, 0);
          this.resetPreviewLayers(card);
          return;
        }

        const primaryImage = card.querySelector('[data-product-card-primary-image]');
        const hoverImage = card.querySelector('[data-product-card-hover-image]');

      [primaryImage, hoverImage].forEach((image) => {
        if (!image) return;

        image.removeAttribute('srcset');
        image.src = state.defaultImage.src;

        if (state.defaultImage.alt) {
          image.alt = state.defaultImage.alt;
        }

        if (Number.isFinite(state.defaultImage.width) && state.defaultImage.width > 0) {
          image.width = state.defaultImage.width;
        }

        if (Number.isFinite(state.defaultImage.height) && state.defaultImage.height > 0) {
          image.height = state.defaultImage.height;
        }
        });

        state.currentPreviewIndex = 0;
        this.updatePreviewDots(card, 0);
        this.resetPreviewLayers(card);
      },

      getImageDataFromSwatch(swatch) {
        if (!swatch) return null;

      return {
        src: swatch.dataset.imageSrc || '',
        alt: swatch.dataset.imageAlt || '',
        width: Number(swatch.dataset.imageWidth || 0),
        height: Number(swatch.dataset.imageHeight || 0)
      };
    },

    applyImageData(image, imageData) {
      if (!image || !imageData || !imageData.src) return;

      image.removeAttribute('srcset');
      image.src = imageData.src;

      if (imageData.alt) {
        image.alt = imageData.alt;
      }

      if (Number.isFinite(imageData.width) && imageData.width > 0) {
        image.width = imageData.width;
      }

      if (Number.isFinite(imageData.height) && imageData.height > 0) {
        image.height = imageData.height;
      }
    },

      finishPreviewAnimation(card) {
        const state = this.cardStates.get(card);
        if (!state) return;

        if (state.previewAnimationTimer) {
          window.clearTimeout(state.previewAnimationTimer);
          state.previewAnimationTimer = null;
        }

        state.isAnimating = false;
        this.resetPreviewLayers(card);
      },

      animatePreviewChange(card, swatch, movement) {
        const state = this.cardStates.get(card);
        if (!state) {
          this.updateCardImages(card, swatch);
          return;
        }

      const primaryImage = card.querySelector('[data-product-card-primary-image]');
      const hoverImage = card.querySelector('[data-product-card-hover-image]');
      const imageData = this.getImageDataFromSwatch(swatch);

        if (!primaryImage || !hoverImage || !imageData || !imageData.src) {
          this.updateCardImages(card, swatch);
          return;
        }

        this.finishPreviewAnimation(card);
        this.applyImageData(hoverImage, imageData);
        this.resetPreviewLayers(card);
        hoverImage.style.transform = movement === 'right' ? 'translateX(-100%)' : 'translateX(100%)';
        void hoverImage.offsetWidth;

        primaryImage.style.transition = `transform ${this.previewSlideDuration}ms linear`;
        hoverImage.style.transition = `transform ${this.previewSlideDuration}ms linear`;
        state.isAnimating = true;

        state.previewAnimationTimer = window.setTimeout(() => {
          this.updateCardImages(card, swatch);
          this.resetPreviewLayers(card);
          state.isAnimating = false;
          state.previewAnimationTimer = null;
        }, this.previewSlideDuration);

        requestAnimationFrame(() => {
          primaryImage.style.transform = movement === 'right' ? 'translateX(100%)' : 'translateX(-100%)';
          hoverImage.style.transform = 'translateX(0)';
        });
      },

      handleClick(event) {
        const swatch = event.target.closest('[data-product-card-swatch]');
        if (!swatch) return;

      event.preventDefault();

        const card = swatch.closest('[data-product-card]');
        if (!card) return;

        const state = this.cardStates.get(card);
        if (state) {
          state.hovered = false;
          this.clearPreviewTimers(state);
        }

        card.dataset.haiProductCardUserSelectedSwatch = 'true';
        card.classList.remove('is-preview-hovered');
        this.updateActiveSwatch(card, swatch);
        this.finishPreviewAnimation(card);
        this.updateCardImages(card, swatch);
        this.updateQuickAddVariant(card, swatch);
        this.syncPreviewDotsWithActive(card);
    },

    updateActiveSwatch(card, activeSwatch) {
      card.querySelectorAll('[data-product-card-swatch]').forEach((swatch) => {
        const isActive = swatch === activeSwatch;
        swatch.classList.toggle('is-active', isActive);
        swatch.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    },

    updateCardImages(card, swatch) {
      const imageData = this.getImageDataFromSwatch(swatch);
      if (!imageData || !imageData.src) return;

      const primaryImage = card.querySelector('[data-product-card-primary-image]');
      const hoverImage = card.querySelector('[data-product-card-hover-image]');

      [primaryImage, hoverImage].forEach((image) => {
        this.applyImageData(image, imageData);
      });
    },

    updateQuickAddVariant(card, swatch) {
      const variantId = swatch.dataset.variantId || '';
      if (!variantId) return;

      const isAvailable = swatch.dataset.variantAvailable !== 'false';
      card.dataset.selectedVariantId = variantId;

      const quickAddButton = card.querySelector('[data-quick-add]');
      if (!quickAddButton) return;

      quickAddButton.setAttribute('data-variant-id', variantId);

      if (isAvailable) {
        quickAddButton.disabled = false;
        quickAddButton.removeAttribute('aria-disabled');
        quickAddButton.removeAttribute('title');
      } else {
        quickAddButton.disabled = true;
        quickAddButton.setAttribute('aria-disabled', 'true');
        quickAddButton.setAttribute('title', 'Sold out');
      }
    }
  };

  Hai.quickAdd = {
    init() {
      this.handleClick = this.handleClick.bind(this);
      document.addEventListener('click', this.handleClick);
    },

    async handleClick(event) {
      const button = event.target.closest('[data-quick-add]');
      if (!button) return;

      event.preventDefault();

      if (button.disabled) return;

      const variantId = button.getAttribute('data-variant-id');
      if (!variantId) return;

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');

      try {
        const lineItem = await Hai.cart.addItem(Number(variantId), 1);
        let cart = await Hai.cart.getCart();

        if (Hai.addonCartSync && typeof Hai.addonCartSync.enforce === 'function') {
          cart = await Hai.addonCartSync.enforce(cart, { notify: true });
        }

        await Hai.cartDrawer.applyCartState(cart);

        const title = button.getAttribute('data-product-title') || '';
        const messageBase = Hai.strings.addedToCart || Hai.strings.addToCart || 'Added to cart';
        const message = title ? `${title} ${messageBase}` : messageBase;

        Hai.cartNotification.show(message);
        Hai.cartDrawer.open();

        document.dispatchEvent(new CustomEvent('hai:cart:add:completed', {
          detail: {
            item: lineItem,
            cart: cart
          }
        }));
      } catch (error) {
        console.error('Quick add error:', error);
        Hai.cartNotification.show(Hai.strings.cartError || 'Unable to add to cart');
      } finally {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  };

  // 产品详情脚本已迁移到 assets/product-main.js

  // ===================================
  // 深色模式切换
  // ===================================

  Hai.theme = {
    init() {
      const themeToggle = document.querySelector('[data-theme-toggle]');

      if (!themeToggle) return;

      themeToggle.addEventListener('click', () => {
        const currentTheme = document.documentElement.getAttribute('data-theme');
        const newTheme = currentTheme === 'dark' ? 'light' : 'dark';

        document.documentElement.setAttribute('data-theme', newTheme);
        localStorage.setItem('hai-theme', newTheme);

        // 更新切换按钮状态        
        themeToggle.setAttribute('aria-pressed', newTheme === 'dark');
      });
    }
  };

  // ===================================
  // 可访问性增强 
  // ===================================

  Hai.a11y = {
    // 键盘焦点管理
    init() {
      // ESC键关闭模态框/抽屉
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          const openModal = document.querySelector('[data-modal][open]');
          const openDrawer = document.querySelector('[data-drawer][open]');

          if (openModal) {
            openModal.close();
          }

          if (openDrawer) {
            openDrawer.close();
          }
        }
      });

      // 焦点陷阱 - 模态框      
      this.trapFocus();
    },

    trapFocus() {
      const focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

      document.addEventListener('focus', (e) => {
        const modal = e.target.closest('[data-modal][open]');

        if (!modal) return;

        const focusable = Array.from(modal.querySelectorAll(focusableElements));
        const firstFocusable = focusable[0];
        const lastFocusable = focusable[focusable.length - 1];

        // 监听Tab       
        modal.addEventListener('keydown', (e) => {
          if (e.key !== 'Tab') return;

          if (e.shiftKey) {
            if (document.activeElement === firstFocusable) {
              lastFocusable.focus();
              e.preventDefault();
            }
          } else {
            if (document.activeElement === lastFocusable) {
              firstFocusable.focus();
              e.preventDefault();
            }
          }
        });
      }, true);
    }
  };

  // ===================================
  // 性能监控
  // ===================================

  Hai.performance = {
    init() {
      if (!('PerformanceObserver' in window)) return;

      // 监控核心Web性能指标
      try {
        // Largest Contentful Paint (LCP)
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          console.log('LCP:', lastEntry.renderTime || lastEntry.loadTime);
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

        // First Input Delay (FID)
        const fidObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach(entry => {
            console.log('FID:', entry.processingStart - entry.startTime);
          });
        });
        fidObserver.observe({ entryTypes: ['first-input'] });

        // Cumulative Layout Shift (CLS)
        let clsScore = 0;
        const clsObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (!entry.hadRecentInput) {
              clsScore += entry.value;
              console.log('CLS:', clsScore);
            }
          }
        });
        clsObserver.observe({ entryTypes: ['layout-shift'] });

      } catch (e) {
        console.warn('Performance monitoring error:', e);
      }
    }
  };

  // ===================================
  // 初始化所有功能
  // ===================================


  Hai.init = function () {
    // DOM加载完成后初始化
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.lazyImages.init();
        this.productCardCountdown.init();
        this.flipCountdown.init();
        this.productCard.init();
        this.quickAdd.init();
        this.cartNotification.init();
        this.cartNote.init();
        this.cartDrawer.init();
        this.mainCart.init();
        this.theme.init();
        this.a11y.init();
        this.performance.init();
      });
    } else {
      this.lazyImages.init();
      this.productCardCountdown.init();
      this.flipCountdown.init();
      this.productCard.init();
      this.quickAdd.init();
      this.cartNotification.init();
      this.cartNote.init();
      this.cartDrawer.init();
      this.mainCart.init();
      this.theme.init();
      this.a11y.init();
      this.performance.init();
    }
  };

  // 暴露到全局
  window.Hai = Hai;

  // 自动初始化
  Hai.init();

})();
